import {SetStateAction, useMemo, useState, useCallback} from 'react';
import {useMutation, useQuery} from '@tanstack/react-query';
import {
  PostLoginVariables,
  postBookingSignIn,
  PostLoginErrorResponseData,
  postCreateAppointment,
  deleteAppointment,
  getCities,
  sendOtp,
  ResendOtp,
  validateOtp,
  putForgotPassword,
  PutForgotPasswordVariables,
  putResetPassword,
  PutResetPasswordVariables,
  PostCreateAppointmentErrorResponse,
  getPaymentCard,
  getPreferredPointOfContact,
  getCountries,
  postBlockSlots,
  PostBlockSlotsVariables,
  ValidatePostcodeVariables,
  postValidatePostcode,
  PostBlockSlotsResponseData,
  getCancellationReasons,
  processCardPayment,
  ProcessCardPaymentResponse,
  ProcessCardPaymentRequest,
  getAmount,
  processPaypalPayment,
  capturePaypalPayment,
  createPaymentIntent,
  CreatePaymentIntentResponse,
} from '$api/bookingFlow';
import {ToastType} from '$components/Toast/constant';
import {handleErrorResponse} from '$utils/axios';
import {
  AppointmentDetailNavigatePayload,
  AppSummaryViewChoices,
  BookingData,
  BookingDataUpdater,
  BookingView,
  Country,
} from './types';
import {
  ValidateOtpResponseToBookingData,
  loginResponseToBookingData,
} from './utils';
import {useNavigate} from 'react-router-dom';
import {RoutePath} from '$constants/routes';
import {UserRoleName} from '$modules/admin/constant';
import {useAppDispatch} from '$redux/hooks';
import {loadAuth} from '$redux/authSlice';
import {AxiosError} from 'axios';
import {
  networkErrorToast,
  technicalGlitchToast,
} from '$constants/toastmessages';
import toast from '$utils/toast';
import {OfflineBookingStep} from '$modules/frontdesk/Calendar/OfflineBooking/constant';
import {FormState} from '$modules/frontdesk/Calendar/OfflineBooking/types';
import LocalStorage from '~/utils/localStore';

export function useValidatePostcode(onSuccess: () => void) {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [localError, setLocalError] = useState(false);

  const {mutate, isPending} = useMutation({
    mutationKey: ['validate-postcode'],
    mutationFn: (variables: ValidatePostcodeVariables) =>
      postValidatePostcode(variables),
    onSuccess: () => {
      setLocalError(false);
      onSuccess();
    },
    onError: error => {
      setLocalError(true);
      handleErrorResponse(error, 'Validate Postcode');
    },
    onSettled: () => setDisableSubmit(false),
  });

  const validatePostcode = (postcode: string, country: string) => {
    setDisableSubmit(true);
    mutate({postcode, country});
  };

  const reset = () => {
    setLocalError(false);
  };

  return {
    isLoading: disableSubmit || isPending,
    validatePostcode,
    isError: localError,
    reset,
  };
}

export function useBookingSignIn(
  setActiveView: (view: BookingView) => void,
  setBookingChoices: BookingDataUpdater
) {
  const dispatch = useAppDispatch();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {isPending, mutate} = useMutation({
    mutationKey: ['booking-sign-in'],
    mutationFn: postBookingSignIn,
    onSuccess: responseData => {
      const data = responseData.data;
      LocalStorage.setItem(
        LocalStorage.AUTH_USER_DATA,
        JSON.stringify(responseData)
      );
      const token = data.token;
      const patientData = data.patient;

      const authData = {
        refreshToken: data.refresh_token,
        token: token,
        user: {
          id: patientData.id,
          fullName: patientData.full_name,
          hospitalId: null,
          email: patientData.email,
          roleName: UserRoleName.Patient,
          mobileNumber: patientData.mobile_number,
          profileUrl: null,
          timezone: null,
        },
      };

      dispatch(loadAuth(authData));

      const bookingData = loginResponseToBookingData(responseData);
      setBookingChoices(draft => {
        if (!draft.registered?.email) {
          draft.patientCarerDetails = bookingData.patientCarerDetails;
          draft.patientContactDetail = bookingData.patientContactDetail;
        }

        draft.registered = {
          isRegistered: true,
          email: data.patient.email,
          patientId: data.patient.id,
          patientMobileNumber: data.patient.mobile_number,
        };
      });

      setActiveView(BookingView.PatientCarerDetailsView);
    },
    onError: error =>
      handleErrorResponse<PostLoginErrorResponseData>(error, 'Login'),
    onSettled: () => setDisableSubmit(false),
  });

  const signIn = (variables: PostLoginVariables) => {
    setDisableSubmit(true);
    mutate(variables);
  };

  const {mutate: forgotPasswordMutate} = useMutation({
    mutationKey: ['forgot-password'],
    mutationFn: putForgotPassword,
    onSuccess: data => {
      toast(ToastType.Success, 'Forgot password', data.message);
    },
    onError: error => handleErrorResponse(error, 'Forgot Password'),
    onSettled: () => setDisableSubmit(false),
  });

  const sendForgotPasswordRequest = (variables: PutForgotPasswordVariables) => {
    setDisableSubmit(true);
    forgotPasswordMutate(variables);
  };

  return {
    disableSubmit,
    isPending,
    signIn,
    sendForgotPasswordRequest,
  };
}

export function useAppointment(
  choices: BookingData,
  setActiveView: (view: BookingView) => void,
  setBookingChoices: BookingDataUpdater
) {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {isPending: isCreatePending, mutate: createMutate} = useMutation({
    mutationKey: ['create-appointment'],
    mutationFn: postCreateAppointment,
    onSuccess: data => {
      setBookingChoices(draft => {
        draft.appointmentId = data.data.appointment.id;
        if (draft.consultant) {
          draft.consultant.fullName = data.data.appointment.doctor_name;
        }
      });
      setActiveView(BookingView.AppSummaryView);
    },
    onError: error => {
      if (error instanceof AxiosError) {
        const axiosError =
          error as AxiosError<PostCreateAppointmentErrorResponse>;
        const response = axiosError.response;
        if (response) {
          if (response.status === 500) {
            return technicalGlitchToast();
          }
          if (response.data) {
            const data = response.data;
            const errors = data.errors;
            toast(ToastType.Error, 'Create Appointment', errors);
            return;
          }
        }
        if (axiosError.message === 'Network Error') {
          return networkErrorToast();
        }

        return technicalGlitchToast();
      } else {
        technicalGlitchToast();
      }
    },
    onSettled: () => setDisableSubmit(false),
  });

  const saveAppointment = (variables?: BookingData) => {
    setDisableSubmit(true);

    let payload = {...choices};

    if (variables) payload = {...payload, ...variables};

    if (payload) {
      createMutate({
        choices: payload,
      });
    }
  };

  return {
    isPending: isCreatePending,
    disableSubmit,
    saveAppointment,
  };
}

export function useDeleteAppointment(bookingChoices: AppSummaryViewChoices) {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const navigate = useNavigate();

  const {isPending, mutate} = useMutation({
    mutationKey: ['delete-appointment'],
    mutationFn: deleteAppointment,
    onSuccess: data => {
      toast(ToastType.Success, 'Appointment deleted', data.message);
      navigate(RoutePath.deleteSuccessRoute, {
        state: {
          careType: bookingChoices.care.title,
          date: bookingChoices.date,
        } as AppointmentDetailNavigatePayload,
      });
    },
    onError: error =>
      handleErrorResponse<PostLoginErrorResponseData>(
        error,
        'Delete Appointment'
      ),
    onSettled: () => setDisableSubmit(true),
  });

  const requestDeleteAppointment = () => {
    setDisableSubmit(true);
    mutate({id: bookingChoices.appointmentId});
  };

  return {
    disableSubmit,
    isPending,
    requestDeleteAppointment,
  };
}
export function useCities(countryId?: string) {
  const {data, isLoading, isError} = useQuery({
    queryKey: ['cities', countryId],
    queryFn: () => getCities({countryId}),
  });

  return {
    cities: data?.data || [],
    isLoading,
    isError,
  };
}

export function useSendOtp() {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {isPending, mutate, isSuccess, reset} = useMutation({
    mutationKey: ['send-otp'],
    mutationFn: sendOtp,
    onSuccess: data => {
      toast(ToastType.Success, 'OTP sent', data.message);
    },
    onError: error =>
      handleErrorResponse<PostLoginErrorResponseData>(error, 'Send OTP'),
    onSettled: () => setDisableSubmit(false),
  });

  const requestSendOtp = (email: string) => {
    setDisableSubmit(true);
    mutate(email);
  };

  return {
    disableSubmit,
    isPending,
    requestSendOtp,
    isSuccess,
    reset,
  };
}
export function usePatientResetPassword() {
  const navigate = useNavigate();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate, isPending} = useMutation({
    mutationKey: ['reset-password'],
    mutationFn: putResetPassword,
    onSuccess: data => {
      navigate(RoutePath.indexRoute);
      toast(ToastType.Success, 'Reset password', data.message);
    },
    onError: error => handleErrorResponse(error, 'Patient Reset Password'),
    onSettled: () => setDisableSubmit(false),
  });

  const sendPatientResetPasswordRequest = (
    variables: PutResetPasswordVariables
  ) => {
    setDisableSubmit(true);
    mutate(variables);
  };

  return {
    disableSubmit,
    isPending,
    sendPatientResetPasswordRequest,
  };
}

export function useResendOtp() {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {isPending, mutate} = useMutation({
    mutationKey: ['resend-otp'],
    mutationFn: ResendOtp,
    onSuccess: data => {
      toast(ToastType.Success, 'OTP resent', data.message);
    },
    onError: error =>
      handleErrorResponse<PostLoginErrorResponseData>(error, 'Resend OTP'),
    onSettled: () => setDisableSubmit(false),
  });

  const requestResendOtp = (email: string) => {
    setDisableSubmit(true);
    mutate(email);
  };

  return {
    disableSubmit,
    isPending,
    requestResendOtp,
  };
}

export function useValidateOtp(
  setActiveView: (view: BookingView) => void,
  setBookingChoices: BookingDataUpdater
) {
  const dispatch = useAppDispatch();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {isPending, mutate, isSuccess} = useMutation({
    mutationKey: ['validate-otp'],
    mutationFn: ({email, otp}: {email: string; otp: number}) =>
      validateOtp(email, otp),
    onSuccess: responseData => {
      const data = responseData.data;

      const token = data.token;
      const refreshToken = data.refresh_token;
      const patientData = data.patient;

      const authData = {
        refreshToken: refreshToken,
        token: token,
        user: {
          id: patientData.id,
          fullName: patientData.full_name,
          hospitalId: null,
          email: patientData.email,
          roleName: UserRoleName.Patient,
          mobileNumber: patientData.mobile_number,
          profileUrl: null,
          timezone: null,
        },
      };

      dispatch(loadAuth(authData));

      const isPatientHasInfo = responseData.data.is_patient_with_full_details;

      const bookingData = ValidateOtpResponseToBookingData(responseData);

      setBookingChoices(draft => {
        if (isPatientHasInfo) {
          draft.patientCarerDetails = bookingData.patientCarerDetails;
          draft.patientContactDetail = bookingData.patientContactDetail;
        } else {
          draft.patientCarerDetails = undefined;
          draft.patientContactDetail = undefined;
        }

        draft.registered = {
          isRegistered: false,
          email: data.patient.email,
          patientId: data.patient.id,
          isEmailvalidated: true,
        };
      });
      setActiveView(BookingView.PatientCarerDetailsView);

      toast(ToastType.Success, 'OTP validated', responseData.message);
    },
    onError: error =>
      handleErrorResponse<PostLoginErrorResponseData>(error, 'Validate OTP'),
    onSettled: () => setDisableSubmit(false),
  });

  const requestValidateOtp = (email: string, otp: number) => {
    setDisableSubmit(true);
    mutate({email, otp});
  };

  return {
    disableSubmit,
    isPending,
    requestValidateOtp,
    isSuccess,
  };
}

export function usePaymentCards() {
  const {data, isLoading, isError} = useQuery({
    queryKey: ['payment-cards'],
    queryFn: getPaymentCard,
  });

  const cards = data?.data || [];

  return {cards, isLoading, isError};
}

export function usePreferredPointOfContact() {
  const {data, isLoading, isError} = useQuery({
    queryKey: ['preferred-point-contact'],
    queryFn: getPreferredPointOfContact,
  });

  return {
    preferredPointOfContact: data?.data || [],
    isLoading: isLoading,
    isError,
  };
}

export function useCountries() {
  const {data, isLoading, isError} = useQuery({
    queryKey: ['country-list'],
    queryFn: getCountries,
  });

  const countries: Country[] = useMemo(() => {
    const countryList = data?.data || [];

    return countryList.map(item => ({
      code: item.code,
      name: item.name,
      countryCode: item.country_code,
    }));
  }, [data]);

  return {countries: countries, isLoading, isError};
}

export function useCancellationReasons() {
  const {data, isLoading, isError} = useQuery({
    queryKey: ['cancellation-reasons'],
    queryFn: getCancellationReasons,
  });

  return {
    cancellationReasons: data?.data || [],
    isLoading,
    isError,
    message: data?.message,
  };
}

export function useBlockSlots({
  setActiveStep,
  setActiveView,
  setBookingChoices,
  setOfflineBookingFormData,
  createAppointment,
  offlineBookingFormData,
  onSuccessCallback,
}: {
  setActiveView?: (view: BookingView) => void;
  setActiveStep?: React.Dispatch<SetStateAction<OfflineBookingStep>>;
  setBookingChoices?: BookingDataUpdater;
  setOfflineBookingFormData?: React.Dispatch<SetStateAction<FormState>>;
  createAppointment?: (payload: FormState) => void;
  offlineBookingFormData?: FormState;
  onSuccessCallback?: (data: PostBlockSlotsResponseData) => void;
}) {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate} = useMutation({
    mutationKey: ['block-slots'],
    mutationFn: postBlockSlots,
    onSuccess: data => {
      if (onSuccessCallback) {
        onSuccessCallback(data);
      }
      if (setBookingChoices && setActiveView) {
        setBookingChoices(draft => {
          draft.appointmentId = data.data.appointment.id;
          const consultant = data.data.appointment.consultant;
          draft.consultant = {
            id: consultant.id,
            fullName: consultant.full_name,
            profileUrl: consultant.profile_url,
            doctorDetails: consultant.doctor_details
              ? {
                  bioLink: consultant.doctor_details.bio_link,
                  followupFee: consultant.doctor_details.followup_fee,
                  consultationFee: consultant.doctor_details.consultation_fee,
                  specializations: consultant.doctor_details.specializations,
                  description: consultant.doctor_details.description,
                }
              : null,
            priceLists: consultant.price_lists
              ? {
                  consultationFee: consultant.price_lists?.consultation_fee,
                  followupFee: consultant.price_lists?.followup_fee,
                }
              : null,
          };
        });
        setActiveView(BookingView.ChoicesConfirmationView);
      } else if (setOfflineBookingFormData && setActiveStep) {
        const newAppointmentId = data.data.appointment.id;
        setOfflineBookingFormData((prevState: FormState) => ({
          appointment: {
            ...prevState.appointment,
            id: newAppointmentId,
          },
        }));
        if (createAppointment && offlineBookingFormData) {
          const updatedFormData = {
            ...offlineBookingFormData,
            appointment: {
              ...offlineBookingFormData.appointment,
              id: newAppointmentId,
            },
          };
          createAppointment(updatedFormData);
        }
        setActiveStep(OfflineBookingStep.PaymentDetails);
      }
    },
    onSettled: () => {
      setDisableSubmit(false);
    },
    onError: error => handleErrorResponse(error, 'Block Slots'),
  });

  const sendBlockSlotsRequest = (variables: PostBlockSlotsVariables) => {
    setDisableSubmit(true);
    mutate(variables);
  };

  return {
    disableSubmit,
    sendBlockSlotsRequest,
  };
}
export const useProcessCardPayment = (
  setActiveView: (view: BookingView) => void
) => {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate: processCardPaymentMutation, isPending} = useMutation({
    mutationKey: ['process-card-payment'],
    mutationFn: processCardPayment,
    onSuccess: () => {
      setActiveView(BookingView.AppSummaryView);
    },
    onError: error => {
      handleErrorResponse(error, 'Process Card Payment');
    },
    onSettled: () => setDisableSubmit(false),
  });

  const handleProcessCardPayment = (params: ProcessCardPaymentRequest) => {
    setDisableSubmit(true);
    return new Promise<ProcessCardPaymentResponse>(resolve => {
      processCardPaymentMutation(params, {
        onSuccess: data => {
          setActiveView(BookingView.AppSummaryView);
          resolve(data);
        },
        onError: () => {
          resolve({
            success: false,
            message: 'Card payment failed.',
          });
        },
        onSettled: () => {
          setDisableSubmit(false);
        },
      });
    });
  };

  return {
    processCardPayment: handleProcessCardPayment,
    isProcessingPayment: isPending || disableSubmit,
  };
};

export const usePaymentAmount = (consultantId: string) => {
  return useQuery({
    queryKey: ['payment-amount', consultantId],
    queryFn: async () => {
      if (!consultantId) {
        return 0;
      }
      try {
        const response = await getAmount({
          consultant_id: consultantId,
        });

        if (!response) {
          console.error('Empty response from getAmount API');
          return 0;
        }

        if (!response.data || response.data.amount === undefined) {
          console.error('Amount is undefined in response data:', response);
          return 0;
        }

        if (typeof response.data.amount !== 'number') {
          console.error('Amount is not a number:', response.data.amount);
          return 0;
        }

        return response.data.amount;
      } catch (error) {
        console.error('Error fetching amount:', error);
        return 0;
      }
    },
    enabled: !!consultantId,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });
};

export const useProcessPayPalPayment = () => {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate: processPaypalPaymentMutation, isPending} = useMutation({
    mutationKey: ['process-paypal-payment'],
    mutationFn: processPaypalPayment,
    onError: error => {
      handleErrorResponse(error, 'PayPal Payment');
    },

    onSettled: () => setDisableSubmit(false),
  });

  const processPayPalPayment = async (params: ProcessCardPaymentRequest) => {
    setDisableSubmit(true);
    return new Promise<ProcessCardPaymentResponse>(resolve => {
      processPaypalPaymentMutation(params, {
        onSuccess: data => {
          resolve(data);
        },
        onError: () => {
          resolve({
            success: false,
            message: 'Paypal payment failed.',
          });
        },
        onSettled: () => {
          setDisableSubmit(false);
        },
      });
    });
  };

  return {
    processPayPalPayment,
    isProcessingPayPal: isPending || disableSubmit,
  };
};

export const useCapturePaypalPayment = (
  onResult?: (view: BookingView) => void
) => {
  const {data, mutate, isPending} = useMutation({
    mutationKey: ['capture-paypal-payment'],
    mutationFn: capturePaypalPayment,
    onSuccess: (data: ProcessCardPaymentResponse) => {
      if (data.success) {
        if (onResult) onResult(BookingView.AppSummaryView);
      } else {
        toast(
          ToastType.Error,
          'Payment Error',
          'PayPal capture failed. Please try again.'
        );
        if (onResult) onResult(BookingView.PaymentFailureView);
      }
    },
    onError: () => {
      toast(
        ToastType.Error,
        'Payment Error',
        'Something went wrong during PayPal capture.'
      );
      if (onResult) onResult(BookingView.PaymentFailureView);
    },
  });

  const capturePaypalMemo = useCallback(
    (order_id: string, is_link_view: boolean) => {
      return new Promise<ProcessCardPaymentResponse>(resolve => {
        mutate(
          {order_id, is_link_view},
          {
            onSuccess: data => resolve(data),
            onError: () =>
              resolve({success: false, message: 'PayPal capture failed.'}),
          }
        );
      });
    },
    [mutate]
  );

  return {
    capturePaypal: capturePaypalMemo,
    isCapturing: isPending,
    data,
  };
};

export const useStripeClientSecret = (
  onClientSecret?: (secret: string) => void
) => {
  const {mutate, isPending} = useMutation({
    mutationKey: ['create-payment-intent'],
    mutationFn: createPaymentIntent,
    onSuccess: (data: CreatePaymentIntentResponse) => {
      if (data.success) {
        onClientSecret?.(data.client_secret);
      } else {
        toast(
          ToastType.Error,
          'Payment Error',
          'Payment intent creation failed. Please try again.'
        );
      }
    },
    onError: error => {
      handleErrorResponse(error, 'Create Payment Intent');
    },
  });

  const createPaymentIntentMemo = useCallback(mutate, [mutate]);

  return {
    createPaymentIntent: createPaymentIntentMemo,
    isCreatingPaymentIntent: isPending,
  };
};
